/**
 * PWA setup — registers service worker using DryPWA's registerSW.
 * Replaces manual SW registration in index.tsx and update polling in VersionChecker.
 *
 * The SW file is at /sw.js (in public/) and implements DryPWA-compatible
 * caching strategies + DrySW-compatible message protocol.
 */

import { registerSW } from '@drystuff/pwa/register';
import type { SWRegistration } from '@drystuff/pwa/register';

let swInstance: SWRegistration | null = null;

/**
 * Initialize PWA service worker registration.
 * Call once from DryContext after database init.
 *
 * Dispatches 'sw-update-available' custom event for the VersionChecker
 * component to listen to instead of polling.
 */
export async function setupPWA(): Promise<SWRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.log('[PWA] Service workers not supported');
    return null;
  }

  // In development, unregister existing SWs and skip registration
  if (import.meta.env.DEV) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      registration.unregister();
      console.log('[PWA] Dev: unregistered existing SW');
    }
    return null;
  }

  try {
    swInstance = await registerSW('/sw.js', {
      onUpdate: () => {
        console.log('[PWA] Update available');
        window.dispatchEvent(new CustomEvent('sw-update-available'));
      },
      onReady: () => {
        console.log('[PWA] Service worker ready');
      },
      onOffline: () => {
        console.log('[PWA] App is offline');
      },
      onOnline: () => {
        console.log('[PWA] App is back online');
      },
      // Check for updates every hour
      updateInterval: 60 * 60 * 1000,
    });

    // Listen for controller change (new SW activated) to trigger reload
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (sessionStorage.getItem('sw_reload_in_progress') === 'true') {
        sessionStorage.removeItem('sw_reload_in_progress');
        return;
      }
      sessionStorage.setItem('sw_reload_in_progress', 'true');
      window.location.reload();
    });

    // Check for a new service worker whenever the tab regains focus (in
    // addition to the hourly interval). A freshly-deployed build then applies
    // within seconds of the user returning to the tab instead of waiting up to
    // an hour — the new SW skips waiting on install and the controllerchange
    // handler above reloads automatically.
    const checkForUpdate = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.update().catch(() => {});
      });
    };
    document.addEventListener('visibilitychange', checkForUpdate);
    window.addEventListener('focus', checkForUpdate);

    return swInstance;
  } catch (error) {
    console.error('[PWA] Registration failed:', error);
    return null;
  }
}

/**
 * Get the current SW registration handle.
 */
export function getSWRegistration(): SWRegistration | null {
  return swInstance;
}

/**
 * Tell the waiting service worker to activate (skip waiting).
 * Called when user clicks "Update Now" in VersionChecker.
 */
export function applyUpdate(): void {
  swInstance?.skipWaiting();
}
